import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ActionButton,
  DrawerContext,
  FormControl,
  Input,
  Toast,
  Toaster,
  ToastModel,
} from '@notino/react-styleguide';

import messages from '@components/Universals/WishlistModifier/messages';
import { useWishlistLists } from '@components/Universals/WishlistModifier/useWishlistLists';
import { getWishlistErrorToastProps } from '@components/Universals/WishlistModifier/useWishlistSelectionDrawer/utils';
import { LIST_NAME_MAX_CHARS } from '@containers/WishlistLists/constants';
import {
  useListDataCache,
  useListsData,
} from '@containers/WishlistLists/hooks/useListsData';
import { trackCreateNewList } from '@containers/WishlistLists/tracking';
import { getWishlistTotalCount } from '@lib/wishlist/getWishlistTotalCount';
import { styled } from '@linaria/react';
import { noop } from '@utils/noop';

const Wrapper = styled.div`
  padding: 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Footer = styled.div`
  flex: 1;
  display: flex;
  justify-content: end;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1.25rem;
`;

const SaveButton = styled(ActionButton)`
  width: 100%;
`;

export const Content = () => {
  const [name, setName] = React.useState('');
  const { createList } = useWishlistLists();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [errorToastProps, setErrorToastProps] = React.useState<
    Parameters<typeof Toaster.error>[0] | null
  >(null);

  const { formatMessage } = useIntl();
  const { closeDrawer } = DrawerContext.useDrawerContext();
  const { addNewListCache } = useListDataCache();
  const { lists } = useListsData();

  const onSave = async () => {
    setError(false);
    setErrorToastProps(null);
    setLoading(true);
    trackCreateNewList({
      interaction: 'wishlist_new',
      wishlistTotalLists: lists.length + 1,
      wishlistTotalProducts: getWishlistTotalCount({ lists }),
    });
    try {
      const list = await createList({ name: name.trim(), productIds: [] });
      addNewListCache(list!);
      closeDrawer();
      Toaster.info({
        icon: 'IconRegularCheckmark',
        content: (
          <FormattedMessage
            {...messages.listCreated}
            values={{
              list: list?.name,
              b: (chunks) => <b style={{ fontWeight: 500 }}>{chunks}</b>,
            }}
          />
        ),
      });
    } catch (e) {
      setError(true);
      setErrorToastProps(getWishlistErrorToastProps(e, lists));
    }
    setLoading(false);
  };

  const trimmedName = name.trim();

  return (
    <Wrapper>
      <FormControl label={formatMessage(messages.listNameLabel)}>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus={true}
        />
      </FormControl>

      <Footer>
        {errorToastProps && (
          <Toast
            {...errorToastProps}
            onClose={noop}
            type={ToastModel.ToastType.Error}
          />
        )}
        <SaveButton
          onClick={onSave}
          isLoading={loading}
          error={error && !errorToastProps}
          disabled={!trimmedName || trimmedName.length > LIST_NAME_MAX_CHARS}
        >
          <FormattedMessage {...messages.createButton} />
        </SaveButton>
      </Footer>
    </Wrapper>
  );
};
